import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Portal } from '../helpers/Portal';
const Wrapper = styled.div `
  position: relative;
  max-width: fit-content;
`;
const TooltipWrapper = styled.div `
  background-color: ${({ theme }) => theme.secondary.blendToForeground(1, 950)};
  backdrop-filter: blur(4px);
  color: ${({ theme }) => theme.background};
  border-radius: ${({ theme }) => theme.style.tooltipBorderRadius};
  padding: ${({ theme }) => theme.style.tooltipPadding};
  font-size: ${({ theme }) => theme.style.tooltipFontSize};
  line-height: ${({ theme }) => theme.style.tooltipLineHeight};
  font-weight: ${({ theme }) => theme.style.tooltipFontWeight};
  width: ${({ $defaultWidth }) => $defaultWidth};
  max-width: ${({ $maxWidth }) => $maxWidth};
  pointer-events: none;
  user-select: none;
  animation-duration: 0.2s;
  animation-fill-mode: forwards;
  transform: var(--end-translation);
  z-index: 5;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  filter: drop-shadow(0px 16px 32px rgba(0, 0, 0, 0.15));
  --tooltip-arrow-half-width: 9px;
  --tooltip-arrow-min-center: calc(${({ theme }) => theme.style.tooltipPadding} + var(--tooltip-arrow-half-width));
  --tooltip-arrow-max-center: calc(
    100% - ${({ theme }) => theme.style.tooltipPadding} - var(--tooltip-arrow-half-width)
  );

  &:before {
    content: '';
    width: 18px;
    height: 8px;
    position: absolute;
    background-color: inherit;
    backdrop-filter: blur(4px);
    mask-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 18 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.515625 0H17.4862L11.8293 5.65685C10.2672 7.21895 7.73458 7.21895 6.17248 5.65685L0.515625 0Z' fill='black'/%3E%3C/svg%3E");
  }

  small {
    display: block;
    font-size: ${({ theme }) => theme.style.tooltipDescriptionFontSize};
    font-weight: normal;
    color: ${({ theme }) => theme.secondary.blendToBackground(400)};
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: var(--start-translation);
    }
    to {
      opacity: 1;
      transform: var(--end-translation);
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  ${({ $isOpen }) => $isOpen
    ? css `
          animation-name: fadeIn;
        `
    : css `
          animation-name: fadeOut;
        `}

  ${({ $placement }) => $placement.includes('left') &&
    css `
      --start-translation: translate(
        calc(-4px + ${({ $offsetX }) => `${$offsetX}px`}),
        ${({ $offsetY }) => `${$offsetY}px`}
      );
      --end-translation: translate(
        calc(-8px + ${({ $offsetX }) => `${$offsetX}px`}),
        ${({ $offsetY }) => `${$offsetY}px`}
      );

      &:before {
        left: 100%;
        top: 50%;
        transform: translate(-5px, -50%) rotate(-90deg) translateZ(0);
      }
    `}

  ${({ $placement }) => $placement.includes('top') &&
    css `
      --start-translation: translate(
        ${({ $offsetX }) => `${$offsetX}px`},
        calc(-4px + ${({ $offsetY }) => `${$offsetY}px`})
      );
      --end-translation: translate(
        ${({ $offsetX }) => `${$offsetX}px`},
        calc(-8px + ${({ $offsetY }) => `${$offsetY}px`})
      );

      &:before {
        left: 50%;
        top: 100%;
        transform: translate(calc(-50% + ${({ $offsetArrowX }) => `${$offsetArrowX}px`}), 0) rotate(0) translateZ(0);
      }
    `}

    ${({ $placement }) => $placement.includes('top-start') &&
    css `
      --start-translation: translate(
        ${({ $offsetX }) => `${$offsetX}px`},
        calc(-4px + ${({ $offsetY }) => `${$offsetY}px`})
      );
      --end-translation: translate(
        ${({ $offsetX }) => `${$offsetX}px`},
        calc(-8px + ${({ $offsetY }) => `${$offsetY}px`})
      );

      &:before {
        left: ${({ theme }) => theme.style.tooltipPadding};
        top: 100%;
        transform: translate(calc(${({ $offsetArrowX }) => `${$offsetArrowX}px`}), 0) rotate(0) translateZ(0);
      }
    `}

    ${({ $placement }) => $placement.includes('top-end') &&
    css `
      --start-translation: translate(
        ${({ $offsetX }) => `${$offsetX}px`},
        calc(-4px + ${({ $offsetY }) => `${$offsetY}px`})
      );
      --end-translation: translate(
        ${({ $offsetX }) => `${$offsetX}px`},
        calc(-8px + ${({ $offsetY }) => `${$offsetY}px`})
      );

      &:before {
        right: ${({ theme }) => theme.style.tooltipPadding};
        left: auto;
        top: 100%;
        transform: translate(calc(${({ $offsetArrowX }) => `${$offsetArrowX}px`}), 0) rotate(0) translateZ(0);
      }
    `}

  ${({ $placement }) => $placement.includes('right') &&
    css `
      --start-translation: translate(
        calc(4px + ${({ $offsetX }) => `${$offsetX}px`}),
        ${({ $offsetY }) => `${$offsetY}px`}
      );
      --end-translation: translate(
        calc(8px + ${({ $offsetX }) => `${$offsetX}px`}),
        ${({ $offsetY }) => `${$offsetY}px`}
      );

      &:before {
        right: 100%;
        top: 50%;
        transform: translate(5px, -50%) rotate(90deg) translateZ(0);
      }
    `}

  ${({ $placement }) => $placement.includes('bottom') &&
    css `
      --start-translation: translate(
        ${({ $offsetX }) => `${$offsetX}px`},
        calc(4px + ${({ $offsetY }) => `${$offsetY}px`})
      );
      --end-translation: translate(
        ${({ $offsetX }) => `${$offsetX}px`},
        calc(8px + ${({ $offsetY }) => `${$offsetY}px`})
      );

      &:before {
        left: clamp(
          var(--tooltip-arrow-min-center),
          calc(50% + ${({ $offsetArrowX }) => `${$offsetArrowX}px`}),
          var(--tooltip-arrow-max-center)
        );
        bottom: 100%;
        transform: translate(-50%, 0) rotate(180deg) translateZ(0);
      }
    `}
`;
export const Tooltip = (props) => {
    const wrapperRef = useRef(null);
    const intervalRef = useRef(null);
    const portalRef = useRef(null);
    const [isOpenState, setIsOpenState] = useState(props.isOpen ?? false);
    const [shouldRender, setShouldRender] = useState(props.isOpen ?? false);
    const styleProps = {
        $isOpen: props.isOpen ?? isOpenState,
        $placement: props.placement ?? 'top',
        $defaultWidth: props.defaultWidth ?? 'max-content',
        $maxWidth: props.maxWidth ?? '220px',
        $offsetX: props.offsetX ?? 0,
        $offsetY: props.offsetY ?? 0,
        $offsetArrowX: props.offsetArrowX ?? 0,
    };
    useEffect(() => {
        if (styleProps.$isOpen) {
            setShouldRender(true);
        }
    }, [styleProps.$isOpen]);
    const handleAnimationEnd = useCallback(() => {
        if (!styleProps.$isOpen) {
            setShouldRender(false);
        }
    }, [styleProps.$isOpen]);
    const handleShow = useCallback((value) => {
        if (!value) {
            clearTimeout(intervalRef.current);
            intervalRef.current = null;
            setIsOpenState(false);
            return;
        }
        if (intervalRef.current) {
            return;
        }
        portalRef.current?.update();
        intervalRef.current = setTimeout(() => setIsOpenState(true), props.delay ?? 0);
    }, [props.delay, intervalRef.current, portalRef.current, setIsOpenState]);
    const handleHoverChange = useCallback((value) => {
        if (props.triggerClick) {
            return;
        }
        handleShow(value);
    }, [props.triggerClick]);
    const handleClick = useCallback((e) => {
        if (!props.triggerClick) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        handleShow(!isOpenState);
    }, [props.triggerClick, isOpenState]);
    const handleDocumentClick = useCallback(() => {
        if (!props.triggerClick || !isOpenState) {
            return;
        }
        setIsOpenState(false);
    }, [props.triggerClick, isOpenState]);
    useEffect(() => {
        document.addEventListener('click', handleDocumentClick);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(Wrapper, { onPointerEnter: () => handleHoverChange(true), onPointerMove: () => handleHoverChange(true), onPointerLeave: () => handleHoverChange(false), onClick: handleClick, ref: wrapperRef, className: props.className }, props.children),
        shouldRender && (React.createElement(Portal, { reference: wrapperRef, placement: styleProps.$placement, restrictFlipCross: true, restrictFlipMain: true, pointerEventsNone: true, zIndex: props.zIndex, ref: portalRef, shouldCalculate: { is: shouldRender, timeout: 200 } },
            React.createElement(TooltipWrapper, { ...styleProps, onAnimationEnd: handleAnimationEnd }, props.content)))));
};
//# sourceMappingURL=Tooltip.js.map